import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, Button, Box, Typography,
    TextField, FormControl, InputLabel, Select, MenuItem, Alert, CircularProgress, Autocomplete, Chip
} from '@mui/material';
import EmailBuilder, { EmailBuilderValue } from 'components/TemplateEditor/EmailTemplate/EmailBuilder/EmailBuilder';
import { apiClient } from 'utils/ApiClient';
import { generateThumbnailFromHtml } from 'utils/ThumbnailGenerator';

const SYSTEM_TEMPLATE_PREFIX = 'IUST-';

const TEMPLATE_CATEGORIES = [
    { value: 'transactional', label: 'Transactional' },
    { value: 'promotional', label: 'Promotional' },
    { value: 'service', label: 'Service' },
    { value: 'system', label: 'System' },
];

type CountryOption = { code: string; label: string; dialCode: string };

interface SystemEmailEditDialogProps {
    open: boolean;
    onClose: () => void;
    template: any;
    onSuccess: () => void;
}

const stripPrefix = (name: string | undefined): string => {
    if (!name) return '';
    return name.startsWith(SYSTEM_TEMPLATE_PREFIX) ? name.substring(SYSTEM_TEMPLATE_PREFIX.length) : name;
};

export const SystemEmailEditDialog: React.FC<SystemEmailEditDialogProps> = ({
    open, onClose, template, onSuccess
}) => {
    const [currentTemplate, setCurrentTemplate] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [emailBuilderValue, setEmailBuilderValue] = useState<EmailBuilderValue | null>(null);
    const [countries, setCountries] = useState<CountryOption[]>([]);

    useEffect(() => {
        if (open && countries.length === 0) {
            apiClient.get('/auth/countries').then((res: any) => {
                if (Array.isArray(res.data)) setCountries(res.data);
            }).catch(() => {});
        }
    }, [open]);

    useEffect(() => {
        if (open && template) {
            setCurrentTemplate({
                ...template,
                templateName: template.templateName || '',
                templateType: template.templateType || 'EMAIL',
                accessLevel: template.accessLevel || 'public',
                emailSubject: template.emailSubject || '',
                templateContent: template.templateContent || '',
                emailBuilderConfig: template.emailBuilderConfig || null,
                category: template.category || '',
                country: template.country || '',
                keywords: Array.isArray(template.keywords) ? template.keywords : [],
            });
            setError(null);
        }
    }, [open, template]);

    const handleTextFieldChange = (key: string, value: any) => {
        setCurrentTemplate((prev: any) => prev ? { ...prev, [key]: value } : null);
    };

    const handleEmailBuilderChange = (value: EmailBuilderValue) => {
        setEmailBuilderValue(value);
        handleTextFieldChange('emailBuilderConfig', value);
    };

    const validateForm = (): boolean => {
        if (!currentTemplate?.templateName?.trim()) { setError('Template Name is required'); return false; }
        if (!currentTemplate?.emailSubject?.trim()) { setError('Email Subject is required'); return false; }
        setError(null);
        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) return;
        setLoading(true);
        setError(null);
        try {
            const htmlContent = currentTemplate.templateContent || '';
            let thumbnailPayload: { data: string; mimeType: string } | undefined;
            if (htmlContent.trim()) {
                const dataUrl = await generateThumbnailFromHtml(htmlContent);
                if (dataUrl) {
                    thumbnailPayload = { data: dataUrl, mimeType: 'image/png' };
                }
            }

            await apiClient.put(`/iu/systemtemplate/${template._id}`, {
                templateName: stripPrefix(currentTemplate.templateName),
                templateType: currentTemplate.templateType,
                accessLevel: currentTemplate.accessLevel,
                emailSubject: currentTemplate.emailSubject,
                templateContent: htmlContent,
                emailBuilderConfig: currentTemplate.emailBuilderConfig || emailBuilderValue || null,
                mediaAttachments: template.mediaAttachments || [],
                ...(thumbnailPayload ? { thumbnail: thumbnailPayload } : {}),
                category: currentTemplate?.category || '',
                country: currentTemplate?.country || '',
                keywords: Array.isArray(currentTemplate?.keywords) ? currentTemplate.keywords : [],
            });
            onSuccess();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while updating the template');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) { setError(null); setCurrentTemplate(null); setEmailBuilderValue(null); onClose(); }
    };

    if (!currentTemplate) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
                <DialogContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress />
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={handleClose} fullScreen
            sx={{ '& .MuiDialog-paper': { margin: 0, maxWidth: '100vw', maxHeight: '100vh', width: '100vw', height: '100vh' } }}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, px: 2, minHeight: 'auto' }}>
                <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Email Builder : {stripPrefix(currentTemplate?.templateName)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                    <Button onClick={handleClose} variant="outlined" color="secondary" size="small" disabled={loading}
                        sx={{ py: 0.25, px: 1.5, fontSize: '0.75rem', minHeight: 28 }}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" color="success" size="small" disabled={loading}
                        sx={{ py: 0.25, px: 1.5, fontSize: '0.75rem', minHeight: 28 }}>
                        {loading ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ height: 'calc(100vh - 52px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ height: '100%', minHeight: 0 }}>
                    {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, height: '100%', minHeight: 0 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel>Access Level</InputLabel>
                                <Select size="small" label="Access Level" value={currentTemplate?.accessLevel || ''}
                                    onChange={e => setCurrentTemplate((prev: any) => prev ? { ...prev, accessLevel: e.target.value } : null)}>
                                    <MenuItem value="public">Public</MenuItem>
                                    <MenuItem value="private">Private</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                select
                                size="small"
                                label="Category"
                                value={currentTemplate?.category || ''}
                                onChange={e => setCurrentTemplate((prev: any) => prev ? { ...prev, category: e.target.value } : null)}
                                fullWidth
                                SelectProps={{
                                    displayEmpty: true,
                                    renderValue: (v) => v ? (TEMPLATE_CATEGORIES.find(c => c.value === v)?.label ?? String(v)) : 'Select category',
                                    MenuProps: { PaperProps: { sx: { '& .MuiMenuItem-root': { py: 1 } } } },
                                }}
                                InputLabelProps={{ shrink: true }}
                            >
                                {TEMPLATE_CATEGORIES.map(cat => (
                                    <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                                ))}
                            </TextField>
                            <Autocomplete
                                size="small"
                                options={countries}
                                getOptionLabel={(option) => `${option.label} (${option.dialCode})`}
                                value={countries.find(c => c.code === currentTemplate?.country) || null}
                                onChange={(_e, newValue) => setCurrentTemplate((prev: any) => prev ? { ...prev, country: newValue?.code || '' } : null)}
                                renderInput={(params) => <TextField {...params} label="Country" />}
                                isOptionEqualToValue={(option, value) => option.code === value.code}
                                fullWidth
                            />
                        </Box>
                        <Autocomplete
                            multiple
                            freeSolo
                            size="small"
                            options={[]}
                            value={currentTemplate?.keywords || []}
                            onChange={(_e, newValue) => setCurrentTemplate((prev: any) => prev ? { ...prev, keywords: newValue as string[] } : null)}
                            renderTags={(value, getTagProps) =>
                                value.map((option: string, index: number) => (
                                    <Chip size="small" label={option} {...getTagProps({ index })} key={index} />
                                ))
                            }
                            renderInput={(params) => <TextField {...params} label="Keywords" placeholder="Type and press Enter" />}
                        />
                        <TextField label="Email Subject" value={currentTemplate?.emailSubject || ''}
                            onChange={e => handleTextFieldChange('emailSubject', e.target.value)}
                            fullWidth size="small" required />
                        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ flex: 1, minHeight: 0 }}>
                                <EmailBuilder
                                    value={currentTemplate?.emailBuilderConfig || undefined}
                                    onChange={(value: EmailBuilderValue) => handleEmailBuilderChange(value)}
                                    onExportHtml={(html: string) => handleTextFieldChange('templateContent', html)}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};
